import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import {
  Box,
  IconButton,
  InputBase,
  Tooltip,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { CopilotConversation } from '@/features/copilot/copilotConversationStore'

interface ConversationDrawerProps {
  open: boolean
  conversations: CopilotConversation[]
  currentId: string | null
  onClose: () => void
  onNewChat: () => void
  onSelect: (id: string) => void
  onRename: (id: string, title: string) => void
  onDelete: (id: string) => void
}

const formatRelativeTime = (timestamp: number): string => {
  const diff = Date.now() - timestamp
  if (diff < 60_000) return 'just now'
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`
  if (diff < 604_800_000) return `${Math.floor(diff / 86_400_000)}d ago`
  return new Date(timestamp).toLocaleDateString()
}

const ConversationDrawer = ({
  open,
  conversations,
  currentId,
  onClose,
  onNewChat,
  onSelect,
  onRename,
  onDelete,
}: ConversationDrawerProps) => {
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const renameInputRef = useRef<HTMLInputElement | null>(null)

  const startRename = useCallback((conv: CopilotConversation) => {
    setRenamingId(conv.id)
    setRenameValue(conv.title)
    setConfirmDeleteId(null)
  }, [])

  const cancelRename = useCallback(() => {
    setRenamingId(null)
    setRenameValue('')
  }, [])

  const commitRename = useCallback(() => {
    if (!renamingId) return
    onRename(renamingId, renameValue)
    setRenamingId(null)
    setRenameValue('')
  }, [renamingId, renameValue, onRename])

  const handleDeleteClick = useCallback(
    (id: string) => {
      if (confirmDeleteId === id) {
        onDelete(id)
        setConfirmDeleteId(null)
        return
      }
      setConfirmDeleteId(id)
    },
    [confirmDeleteId, onDelete],
  )

  useEffect(() => {
    if (!renamingId) return
    const handle = window.setTimeout(() => {
      renameInputRef.current?.focus()
      renameInputRef.current?.select()
    }, 0)
    return () => window.clearTimeout(handle)
  }, [renamingId])

  useEffect(() => {
    if (!confirmDeleteId) return
    const timer = window.setTimeout(() => setConfirmDeleteId(null), 4000)
    return () => window.clearTimeout(timer)
  }, [confirmDeleteId])

  useEffect(() => {
    if (!open) {
      setRenamingId(null)
      setRenameValue('')
      setConfirmDeleteId(null)
    }
  }, [open])

  if (!open) return null

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        width: { xs: '100%', sm: 280 },
        bgcolor: 'background.paper',
        borderRight: '1px solid',
        borderColor: 'border.devider',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 3,
        boxShadow: '4px 0 12px rgba(0,0,0,0.08)',
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'border.devider',
          gap: 1,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ color: 'text.main', fontWeight: 600 }}
        >
          Conversations
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.25 }}>
          <Tooltip title="New chat">
            <IconButton size="small" onClick={onNewChat}>
              <AddCommentOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          px: 0.5,
          py: 0.75,
        }}
      >
        {conversations.length === 0 && (
          <Typography
            variant="caption"
            sx={{
              color: 'grey.600',
              display: 'block',
              textAlign: 'center',
              mt: 3,
              px: 2,
            }}
          >
            No conversations yet — start chatting.
          </Typography>
        )}

        {conversations.map((conv) => {
          const isActive = conv.id === currentId
          const isRenaming = conv.id === renamingId
          const isConfirmingDelete = conv.id === confirmDeleteId
          return (
            <Box
              key={conv.id}
              sx={{
                position: 'relative',
                px: 1,
                py: 0.75,
                mb: 0.25,
                borderRadius: 1,
                cursor: isRenaming ? 'default' : 'pointer',
                bgcolor: isActive ? 'background.overrideDefault' : 'transparent',
                border: '1px solid',
                borderColor: isActive ? 'border.main' : 'transparent',
                '&:hover': {
                  bgcolor: isActive
                    ? 'background.overrideDefault'
                    : 'action.hover',
                  '& .convo-actions': { opacity: 1 },
                },
                '& .convo-actions': {
                  opacity: isConfirmingDelete || isRenaming ? 1 : 0,
                  transition: 'opacity 120ms ease',
                },
              }}
              onClick={() => {
                if (isRenaming) return
                onSelect(conv.id)
              }}
            >
              {isRenaming ? (
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <InputBase
                    inputRef={renameInputRef}
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        commitRename()
                      } else if (e.key === 'Escape') {
                        e.preventDefault()
                        cancelRename()
                      }
                    }}
                    fullWidth
                    sx={{
                      fontSize: 12.5,
                      color: 'text.main',
                      px: 0.5,
                      py: 0.25,
                      border: '1px solid',
                      borderColor: 'primary.main',
                      borderRadius: 0.5,
                    }}
                  />
                  <Tooltip title="Save">
                    <IconButton size="small" onClick={commitRename}>
                      <CheckIcon fontSize="inherit" sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Cancel">
                    <IconButton size="small" onClick={cancelRename}>
                      <CloseIcon fontSize="inherit" sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                <>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.main',
                      fontSize: 12.5,
                      fontWeight: isActive ? 600 : 400,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      pr: 6,
                    }}
                  >
                    {conv.title || 'Untitled'}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'grey.600',
                      fontSize: 10.5,
                      display: 'block',
                      mt: 0.25,
                    }}
                  >
                    {formatRelativeTime(conv.updatedAt)}
                  </Typography>
                  <Box
                    className="convo-actions"
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      display: 'flex',
                      gap: 0.25,
                      bgcolor: isActive
                        ? 'background.overrideDefault'
                        : 'background.paper',
                      borderRadius: 0.5,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Tooltip title="Rename">
                      <IconButton
                        size="small"
                        onClick={() => startRename(conv)}
                        sx={{ p: 0.25 }}
                      >
                        <EditOutlinedIcon
                          fontSize="inherit"
                          sx={{ fontSize: 14 }}
                        />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={
                        isConfirmingDelete
                          ? 'Click again to confirm'
                          : 'Delete'
                      }
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(conv.id)}
                        sx={{
                          p: 0.25,
                          color: isConfirmingDelete ? 'error.main' : undefined,
                        }}
                      >
                        <DeleteOutlineIcon
                          fontSize="inherit"
                          sx={{ fontSize: 14 }}
                        />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </>
              )}
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export default ConversationDrawer
